import type { CheckoutServiceOption, CheckoutServiceOptionPriceAssignationParticipant } from '../../../domain/types'
import type {
  APIBookingDraft,
  APIBookingDraftBusServiceOption,
  APIBookingDraftCarRentalServiceOption,
  APIBookingDraftClothingServiceOption,
  APIBookingDraftLuggageServiceOption,
  APIBookingDraftParticipant,
  APIBookingDraftService,
  APIBookingDraftServiceCancellationInsuranceOption,
  APIBookingDraftServiceOption,
  APIBookingDraftServiceOptionParticipantPrice,
  APIBookingDraftSkiAncillaryServiceOption,
  ServiceRenderType,
} from '@/scopes/checkout/infrastructure/api/types'

export interface ServiceOptionMapper {
  map: (
    option:
      | APIBookingDraftServiceOption
      | APIBookingDraftLuggageServiceOption
      | APIBookingDraftServiceCancellationInsuranceOption
      | APIBookingDraftBusServiceOption
      | APIBookingDraftCarRentalServiceOption
      | APIBookingDraftSkiAncillaryServiceOption
      | APIBookingDraftClothingServiceOption,
    service: APIBookingDraftService,
    bookingDraft: APIBookingDraft
  ) => CheckoutServiceOption
}

export function createServiceOptionMapper() {
  function map(
    { displayText, bestValueText, code, price, prices, percentage }: APIBookingDraftServiceOption,
    { isPricePerPerson, renderType }: APIBookingDraftService,
    bookingDraft: APIBookingDraft,
  ) {
    return {
      label: displayText,
      value: code,
      price: mapOptionPrice(price, prices ?? [], isPricePerPerson ?? false, renderType),
      rawPrice: price,
      percentage: percentage ?? undefined,
      ...(mapPriceAssignments(renderType, bookingDraft.participants ?? [], prices ?? []) || undefined),
      ...(bestValueText && { badge: bestValueText }),
    }
  }

  function mapOptionPrice(
    price: number,
    prices: APIBookingDraftServiceOptionParticipantPrice[],
    isPricePerPerson: boolean,
    renderType: ServiceRenderType,
  ): number {
    if (
      !isPricePerPerson
      || (isPricePerPerson && prices.length === 0)
      || (renderType === 'PerParticipant'
        && isPricePerPerson
        && prices.length > 0
      )
    ) {
      return price
    }

    return prices?.reduce((total, item) => total + item.price * item.participantIds.length, 0)
  }

  function mapPriceAssignments(
    renderType: ServiceRenderType,
    participants: APIBookingDraftParticipant[],
    prices: APIBookingDraftServiceOptionParticipantPrice[],
  ): { priceAssignments: CheckoutServiceOptionPriceAssignationParticipant[] } | undefined {
    const pricesWithParticipantsFiltered = prices.filter(itemPrice => itemPrice.participantIds.length > 0)

    if (
      renderType !== 'PerParticipant'
      || !prices
      || prices.length === 0
      || participants.length === 0
      || (renderType === 'PerParticipant' && prices.length > 0 && pricesWithParticipantsFiltered.length === 1)
    ) {
      return
    }

    const priceAssignments = prices.map(({ price, participantIds }) => {
      return {
        price,
        participantIds: participantIds.map(id => id.toString()),
      }
    })
    return { priceAssignments }
  }

  return { map }
}
